import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Checkbox,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  FormField,
  QueryStateBoundary,
} from "@/components/ui";
import { getErrorMessage } from "@/api/client";
import { toastError, toastSuccess } from "@/components/ui/Toast";
import { centsToUsd } from "@/lib/money";
import { useContact } from "@/api/hooks/useContacts";
import { useResources } from "@/api/hooks/useBookings";
import {
  useActivateMembership,
  useCancelMembership,
  useCreateMembershipPortalLink,
  useDayPasses,
  useMembership,
  useMembershipBalance,
  useMembershipSessions,
  useMembershipTiers,
  usePassPacks,
  usePauseMembership,
  useRedeemPassPack,
  useResumeMembership,
} from "@/api/hooks/useMemberships";
import {
  MEMBERSHIP_STATUS_BADGE_VARIANT,
  MEMBERSHIP_STATUS_LABELS,
  STUDIO_SESSION_SOURCE_LABELS,
} from "@/api/types/membership";
import { formatBalanceSummary, formatTimeRange, formatWeekRange } from "./hourMath";
import { BookSessionDialog } from "./BookSessionDialog";

export function MemberDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const { data: membership, isLoading, error, refetch } = useMembership(id);
  const { data: contact } = useContact(membership?.contact_id);
  const { data: resources } = useResources();
  const studioResource = resources?.find((r) => r.kind === "studio");
  const { data: tiers } = useMembershipTiers(false);
  const tier = tiers?.find((t) => t.id === membership?.tier_id);

  const { data: balance } = useMembershipBalance(membership?.status === "active" ? membership.id : undefined);
  const { data: sessions } = useMembershipSessions(id);
  const { data: dayPasses } = useDayPasses(membership?.contact_id);
  const { data: passPacks } = usePassPacks(membership?.contact_id);

  const [activateOpen, setActivateOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [bookOpen, setBookOpen] = useState(false);

  const pauseMembership = usePauseMembership();
  const resumeMembership = useResumeMembership();
  const portalLink = useCreateMembershipPortalLink();
  const redeemPack = useRedeemPassPack();

  async function handlePause() {
    if (!membership) return;
    try {
      await pauseMembership.mutateAsync(membership.id);
      toastSuccess("Membership paused");
    } catch (err) {
      toastError("Couldn't pause membership", getErrorMessage(err));
    }
  }

  async function handleResume() {
    if (!membership) return;
    try {
      await resumeMembership.mutateAsync(membership.id);
      toastSuccess("Membership resumed");
    } catch (err) {
      toastError("Couldn't resume membership", getErrorMessage(err));
    }
  }

  async function handleCopyPortalLink() {
    if (!membership) return;
    try {
      const url = await portalLink.mutateAsync(membership.id);
      await navigator.clipboard.writeText(url);
      toastSuccess("Portal link copied to clipboard");
    } catch (err) {
      toastError("Couldn't create portal link", getErrorMessage(err));
    }
  }

  async function handleRedeemPack(packId: string) {
    const passDate = window.prompt("Redeem a pass for which date? (YYYY-MM-DD)");
    if (!passDate) return;
    try {
      await redeemPack.mutateAsync({ pack_id: packId, pass_date: passDate });
      toastSuccess("Pass redeemed");
    } catch (err) {
      toastError("Couldn't redeem pass", getErrorMessage(err));
    }
  }

  return (
    <div className="p-6 sm:p-8">
      <Link to="/members" className="mb-4 inline-block text-sm text-text-secondary hover:underline">
        ← Back to members
      </Link>

      <QueryStateBoundary isLoading={isLoading} error={error} onRetry={() => void refetch()}>
        {membership && (
          <>
            <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
              <div>
                <h1 className="font-display text-2xl font-bold text-text-primary">{contact?.name ?? "…"}</h1>
                <p className="mt-1 text-sm text-text-secondary">
                  {membership.tier_name}
                  {tier ? ` — ${centsToUsd(tier.price_cents)}/mo` : ""}
                  {membership.storage_assigned ? " · storage included" : ""}
                  {/* Staff-visible only — deliberate, per app/models/membership_tier.py's
                      docstring: the owner's "don't let a prospect compare pricing"
                      requirement is about the PUBLIC/portal surface, not staff. */}
                  {tier?.addon_hour_rate_cents ? ` · add-on hours ${centsToUsd(tier.addon_hour_rate_cents)}/hr` : ""}
                </p>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <Badge variant={MEMBERSHIP_STATUS_BADGE_VARIANT[membership.status]}>
                  {MEMBERSHIP_STATUS_LABELS[membership.status]}
                </Badge>
                {membership.status === "pending" && (
                  <Button size="sm" onClick={() => setActivateOpen(true)} data-testid="activate-membership-button">
                    Activate
                  </Button>
                )}
                {membership.status === "active" && (
                  <>
                    <Button size="sm" variant="secondary" onClick={() => void handlePause()} data-testid="pause-membership-button">
                      Pause
                    </Button>
                    <Button size="sm" variant="danger" onClick={() => setCancelOpen(true)} data-testid="cancel-membership-button">
                      Cancel
                    </Button>
                  </>
                )}
                {membership.status === "paused" && (
                  <>
                    <Button size="sm" onClick={() => void handleResume()} data-testid="resume-membership-button">
                      Resume
                    </Button>
                    <Button size="sm" variant="danger" onClick={() => setCancelOpen(true)} data-testid="cancel-membership-button">
                      Cancel
                    </Button>
                  </>
                )}
                <Button size="sm" variant="secondary" onClick={() => void handleCopyPortalLink()}>
                  Copy portal link
                </Button>
              </div>
            </div>

            {membership.min_term_ends_on && (
              <p className="mb-4 text-xs text-text-muted">
                Minimum term ends {membership.min_term_ends_on}
                {membership.started_on ? ` (started ${membership.started_on})` : ""}
              </p>
            )}

            <div className="grid gap-4 lg:grid-cols-2">
              <Card>
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle>This week's hour bank</CardTitle>
                  {membership.status === "active" && (
                    <Button size="sm" variant="secondary" onClick={() => setBookOpen(true)} data-testid="member-book-session-button">
                      Book session
                    </Button>
                  )}
                </CardHeader>
                <CardContent>
                  {membership.status !== "active" ? (
                    <p className="text-sm text-text-muted">Only active memberships accrue an hour balance.</p>
                  ) : balance ? (
                    <>
                      <p className="text-sm text-text-secondary">{formatWeekRange(balance.week_start, balance.week_end)}</p>
                      <p className="mt-1 text-lg font-semibold text-text-primary" data-testid="member-balance-summary">
                        {formatBalanceSummary(balance)}
                      </p>
                    </>
                  ) : null}
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Pass packs</CardTitle>
                </CardHeader>
                <CardContent>
                  {!passPacks || passPacks.length === 0 ? (
                    <p className="text-sm text-text-muted">No pass packs purchased.</p>
                  ) : (
                    <ul className="space-y-2">
                      {passPacks.map((p) => (
                        <li key={p.id} className="flex items-center justify-between text-sm">
                          <span className="text-text-primary">
                            {p.passes_used}/{p.passes_total} used
                          </span>
                          <span className="text-text-muted">expires {p.expires_at.slice(0, 10)}</span>
                          {p.passes_used < p.passes_total && (
                            <Button size="sm" variant="secondary" onClick={() => void handleRedeemPack(p.id)}>
                              Redeem
                            </Button>
                          )}
                        </li>
                      ))}
                    </ul>
                  )}
                </CardContent>
              </Card>

              <Card className="lg:col-span-2">
                <CardHeader>
                  <CardTitle>Studio sessions</CardTitle>
                </CardHeader>
                <CardContent>
                  {!sessions || sessions.length === 0 ? (
                    <p className="text-sm text-text-muted">No studio sessions booked yet.</p>
                  ) : (
                    <ul className="divide-y divide-border" data-testid="member-sessions-list">
                      {sessions.map((s) => (
                        <li key={s.id} className="flex flex-wrap items-center justify-between gap-2 py-2 text-sm">
                          <span className="text-text-primary">{s.session_date}</span>
                          <span className="text-text-secondary">{formatTimeRange(s.start_time, s.end_time)}</span>
                          <span className="text-text-muted">{STUDIO_SESSION_SOURCE_LABELS[s.source]}</span>
                          <Badge variant={s.status === "cancelled" ? "danger" : s.status === "completed" ? "success" : "info"}>
                            {s.status}
                          </Badge>
                        </li>
                      ))}
                    </ul>
                  )}
                </CardContent>
              </Card>

              <Card className="lg:col-span-2">
                <CardHeader>
                  <CardTitle>Day passes</CardTitle>
                </CardHeader>
                <CardContent>
                  {!dayPasses || dayPasses.length === 0 ? (
                    <p className="text-sm text-text-muted">No day passes on file.</p>
                  ) : (
                    <ul className="divide-y divide-border">
                      {dayPasses.map((dp) => (
                        <li key={dp.id} className="flex items-center justify-between py-2 text-sm">
                          <span className="text-text-primary">{dp.pass_date}</span>
                          <span className="text-text-muted">{dp.source === "pack" ? "from pack" : "single purchase"}</span>
                          <span className="text-text-secondary">{centsToUsd(dp.price_cents)}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </CardContent>
              </Card>
            </div>
          </>
        )}
      </QueryStateBoundary>

      {membership && (
        <>
          <ActivateMembershipDialog
            open={activateOpen}
            onClose={() => setActivateOpen(false)}
            membershipId={membership.id}
          />
          <CancelMembershipDialog
            open={cancelOpen}
            onClose={() => setCancelOpen(false)}
            membershipId={membership.id}
            onCancelled={() => navigate("/members")}
          />
          {studioResource && (
            <BookSessionDialog
              open={bookOpen}
              onClose={() => setBookOpen(false)}
              resourceId={studioResource.id}
              defaultMembershipId={membership.id}
            />
          )}
        </>
      )}
    </div>
  );
}

function ActivateMembershipDialog({
  open,
  onClose,
  membershipId,
}: {
  open: boolean;
  onClose: () => void;
  membershipId: string;
}) {
  const [startedOn, setStartedOn] = useState("");
  const [serverError, setServerError] = useState<string | null>(null);
  const activateMembership = useActivateMembership();

  async function handleSubmit() {
    setServerError(null);
    try {
      const result = await activateMembership.mutateAsync({ id: membershipId, started_on: startedOn || null });
      toastSuccess(`Membership activated — ${result.billing_mode.startsWith("stripe") ? "Stripe subscription created" : "manual billing"}`);
      onClose();
    } catch (err) {
      const message = getErrorMessage(err);
      setServerError(message);
      toastError("Couldn't activate membership", message);
    }
  }

  return (
    <Dialog open={open} onClose={onClose} ariaLabel="Activate membership">
      <DialogContent size="sm">
        <DialogHeader onClose={onClose}>Activate membership</DialogHeader>
        <DialogBody className="space-y-4">
          <FormField
            label="Start date (optional — defaults to today)"
            type="date"
            value={startedOn}
            onChange={(e) => setStartedOn(e.target.value)}
          />
          <p className="text-xs text-text-muted">
            Starts the 3-month minimum-term clock. If Stripe is configured and this tier has a price set, a
            subscription is created automatically; otherwise this is a manual-billing membership.
          </p>
          {serverError && (
            <p className="text-sm text-danger" role="alert">
              {serverError}
            </p>
          )}
        </DialogBody>
        <DialogFooter>
          <Button variant="secondary" onClick={onClose} disabled={activateMembership.isPending}>
            Cancel
          </Button>
          <Button onClick={() => void handleSubmit()} disabled={activateMembership.isPending} data-testid="confirm-activate-membership">
            {activateMembership.isPending ? "Activating…" : "Activate"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CancelMembershipDialog({
  open,
  onClose,
  membershipId,
  onCancelled,
}: {
  open: boolean;
  onClose: () => void;
  membershipId: string;
  onCancelled?: () => void;
}) {
  const [effectiveDate, setEffectiveDate] = useState("");
  const [adminOverride, setAdminOverride] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);
  const cancelMembership = useCancelMembership();

  async function handleSubmit() {
    setServerError(null);
    try {
      await cancelMembership.mutateAsync({
        id: membershipId,
        effective_date: effectiveDate || null,
        admin_override: adminOverride,
      });
      toastSuccess("Membership cancelled");
      onCancelled?.();
      onClose();
    } catch (err) {
      const message = getErrorMessage(err);
      setServerError(message);
      toastError("Couldn't cancel membership", message);
    }
  }

  return (
    <Dialog open={open} onClose={onClose} ariaLabel="Cancel membership">
      <DialogContent size="sm">
        <DialogHeader onClose={onClose}>Cancel membership</DialogHeader>
        <DialogBody className="space-y-4">
          <FormField
            label="Effective date (optional — defaults to today)"
            type="date"
            value={effectiveDate}
            onChange={(e) => setEffectiveDate(e.target.value)}
          />
          <Checkbox
            label="Override the 3-month minimum-term guard"
            checked={adminOverride}
            onChange={(e) => setAdminOverride(e.target.checked)}
          />
          {serverError && (
            <p className="text-sm text-danger" role="alert" data-testid="cancel-membership-error">
              {serverError}
            </p>
          )}
        </DialogBody>
        <DialogFooter>
          <Button variant="secondary" onClick={onClose} disabled={cancelMembership.isPending}>
            Keep membership
          </Button>
          <Button
            variant="danger"
            onClick={() => void handleSubmit()}
            disabled={cancelMembership.isPending}
            data-testid="confirm-cancel-membership"
          >
            {cancelMembership.isPending ? "Cancelling…" : "Cancel membership"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
